import { matchPath } from 'react-router-dom'
import type { RolDeUsuario } from '../servicios/api'
import { hoyLocal, type ValorDeAyuda } from './ContextoDeAyuda'
import {
  aplicaAlRol,
  NOVEDADES,
  TIPS_DE_PANTALLA,
  TIPS_DEL_DIA,
  type NovedadContenido,
  type TipDePantallaContenido,
  type TipDelDiaContenido,
} from './contenidoDeTips'
// La clave y la versión del recorrido son las del recorrido mismo: si sube de versión, los TIPs lo saben solos.
import { CLAVE_DEL_RECORRIDO, VERSION_DEL_RECORRIDO } from './contenidoDelRecorrido'

/**
 * Qué TIP o novedad toca mostrar (RF-1703, RF-1705, RF-1706). Funciones puras sobre el contexto de ayuda, para que
 * los tres componentes digan lo mismo y para probar la regla sin pintar nada.
 */


/**
 * Hay un recorrido en curso: lo pidieron desde «Ayuda», o va a arrancar solo porque el usuario no lo ha visto.
 * Ningún TIP compite con él (RN-68: la ayuda no estorba, y dos ayudas a la vez estorban).
 */
export function recorridoEnCurso(ayuda: ValorDeAyuda): boolean {
  return ayuda.recorridoSolicitado !== null || (ayuda.estado !== null && !ayuda.vioRecorrido(CLAVE_DEL_RECORRIDO, VERSION_DEL_RECORRIDO))
}

/** El TIP no visto de la pantalla en que está el usuario, para su rol. Null si no hay. */
export function tipDePantallaPendiente(ruta: string, ayuda: ValorDeAyuda): TipDePantallaContenido | null {
  return (
    TIPS_DE_PANTALLA.find(
      (t) => matchPath({ path: t.ruta, end: true }, ruta) !== null && aplicaAlRol(t.roles, ayuda.rol) && !ayuda.vioTip(t.clave),
    ) ?? null
  )
}

/**
 * Las novedades no vistas, de la más vieja a la más nueva. Una novedad del día en que el usuario se dio de alta o
 * anterior no se le muestra: cuando él llegó, eso ya estaba (RF-1706).
 */
export function novedadesPendientes(ayuda: ValorDeAyuda, novedades: NovedadContenido[] = NOVEDADES): NovedadContenido[] {
  if (ayuda.estado === null) return []
  const desde = hoyLocal(new Date(ayuda.estado.usuarioDesdeUtc))
  return novedades
    .filter((n) => n.fecha > desde && aplicaAlRol(n.roles, ayuda.rol) && !ayuda.vioNovedad(n.clave))
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
}

/** El TIP del día: rota por fecha, el mismo todo el día y el mismo para todos los del rol. Null si no hay ninguno. */
export function tipDelDia(hoy: string, rol: RolDeUsuario | null, tips: TipDelDiaContenido[] = TIPS_DEL_DIA): TipDelDiaContenido | null {
  const delRol = tips.filter((t) => aplicaAlRol(t.roles, rol))
  if (delRol.length === 0) return null
  const [anio, mes, dia] = hoy.split('-').map(Number)
  const numeroDeDia = Math.floor(Date.UTC(anio, mes - 1, dia) / 86_400_000)
  return delRol[numeroDeDia % delRol.length]
}

/**
 * La ruta del enlace de una novedad. Las rutas de cliente (`:id`) van al cliente con que se está trabajando; sin
 * uno, a la lista de clientes para elegirlo.
 */
export function rutaDeNovedad(ruta: string, clienteId: string): string {
  if (!ruta.includes(':id')) return ruta
  return clienteId ? ruta.replace(':id', clienteId) : '/clientes'
}
